import { useContext } from 'react'

import { faFlag, faListCheck, faShieldAlt } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Divider, Radio, Theme, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Avatar, { ICON_COLOR } from '@scoir/avatar'
import { NonDismissibleDialog } from '@scoir/modals'

import CollegeCrest from 'components/CollegeCrest/index.jsx'

import ShareInterestContext from './ShareInterestProvider/Context'
import YourInfo from './YourInfo.tsx'

const { Body, Footer } = NonDismissibleDialog
const { PrimaryActionButton } = Footer

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    textAlign: 'left',
    flexDirection: 'column',
    borderRadius: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  selected: {
    border: `1px solid ${theme.palette.primary.main}`,
    background: '#EBF5F9',
  },
  unselected: {
    border: `1px solid ${theme.palette.primary.light}`,
  },
  buttonBox: {
    display: 'flex',
    paddingLeft: theme.spacing(0.25),
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  flexBox: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  crest: {
    backgroundColor: '#FFF',
  },
}))

export const StudentShareInterestModal = () => {
  const classes = useStyles()
  const {
    open,
    handleDismiss,
    handleAfterClose,
    selectedCollege,
    handleSave,
    preferenceType,
    setPreferenceType,
    requiredFieldsIncomplete,
    isSaving,
  } = useContext(ShareInterestContext)

  return (
    <NonDismissibleDialog
      open={open}
      onClose={handleDismiss}
      headerText="Let colleges know you’re interested"
      PaperProps={{ onClick: (e) => e.stopPropagation() }}
      TransitionProps={{ onExited: handleAfterClose }}
    >
      <Divider />
      <Body>
        <Box
          display="flex"
          flexDirection="column"
          gridGap="16px"
        >
          <Typography>Request communications from colleges outside of Scoir.</Typography>
          <YourInfo />
        </Box>
        <Button
          fullWidth
          className={`${classes.root} ${
            preferenceType === 'share_with_all_colleges' ? classes.selected : classes.unselected
          }`}
          onClick={() => setPreferenceType('share_with_all_colleges')}
        >
          <Box className={classes.buttonBox}>
            <Box className={classes.flexBox}>
              <Avatar
                size="sm"
                variant="rounded"
                fallbackIcon={<FontAwesomeIcon icon={faFlag} />}
                iconColor={ICON_COLOR.success}
              />
              <Typography variant="button">Share with all colleges on my list</Typography>
            </Box>
            <Radio
              color="primary"
              checked={preferenceType === 'share_with_all_colleges'}
            />
          </Box>
        </Button>
        <Button
          fullWidth
          className={`${classes.root} ${
            preferenceType === 'ask_me_every_time' ? classes.selected : classes.unselected
          }`}
          onClick={() => setPreferenceType('ask_me_every_time')}
        >
          <Box className={classes.buttonBox}>
            <Box className={classes.flexBox}>
              {selectedCollege ? (
                <CollegeCrest
                  size="sm"
                  scid={selectedCollege.Scid}
                  className={classes.crest}
                />
              ) : (
                <Avatar
                  size="sm"
                  variant="rounded"
                  fallbackIcon={<FontAwesomeIcon icon={faListCheck} />}
                  iconColor={ICON_COLOR.info}
                />
              )}
              {selectedCollege ? (
                <Typography variant="button">{`Share with ${selectedCollege.Name} only`}</Typography>
              ) : (
                <Typography variant="button">Ask me each time I follow a college</Typography>
              )}
            </Box>
            <Radio
              color="primary"
              checked={preferenceType === 'ask_me_every_time'}
            />
          </Box>
        </Button>
        <Typography variant="caption">
          <FontAwesomeIcon icon={faShieldAlt} /> <strong>Your privacy matters:</strong> Scoir will never send your
          contact information to colleges without your permission. You can see the colleges you’ve shared with and
          adjust your settings in My Colleges.
        </Typography>
      </Body>
      <Footer>
        <Button
          variant="text"
          color="primary"
          onClick={handleDismiss}
        >
          {selectedCollege ? 'Not Now' : 'Dismiss'}
        </Button>
        <PrimaryActionButton
          disabled={requiredFieldsIncomplete || isSaving}
          onClick={handleSave}
        >
          {selectedCollege ? 'Share' : 'Save'}
        </PrimaryActionButton>
      </Footer>
    </NonDismissibleDialog>
  )
}

export default StudentShareInterestModal
